import { readFile, stat } from 'node:fs/promises';
import { resolve } from 'node:path';
import { parseSessionExportBundle, type SessionExportBundle } from '@gitlab-lsp/workflow-api';
import { UsageError } from '../utils/usage_error';

// A summary-only bundle is a few KB; anything this large is not one.
export const MAX_BUNDLE_BYTES = 20 * 1024 * 1024;

const errnoOf = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : undefined;

/** Exported for tests: provoking a real EACCES is impossible when running as root. */
export const describeReadFailure = (path: string, error: unknown): string => {
  switch (errnoOf(error)) {
    case 'ENOENT':
      return `No session export found at ${path}. Create one with \`duo export --existing-session-id <id>\`.`;
    case 'EISDIR':
      return `${path} is a directory, not a session export file.`;
    case 'EACCES':
    case 'EPERM':
      return `Cannot read ${path}: permission denied.`;
    default:
      return `Cannot read ${path}: ${error instanceof Error ? error.message : String(error)}`;
  }
};

// Read once per path so the bundle the pre-flight check validated is the one the
// provider injects; re-reading lets the file be swapped in between.
const cache = new Map<string, SessionExportBundle>();

/** Exported for tests, which reuse paths across cases within one process. */
export function clearSessionImportCache(): void {
  cache.clear();
}

/**
 * Reads and validates a bundle written by `duo export`.
 *
 * Throws {@link UsageError} on every failure, so callers must run this before
 * the TUI paints: `DefaultSystemContextManager` swallows provider throws, which
 * would turn a bad bundle into a session that silently has no imported context.
 */
export async function readSessionImportBundle(
  path: string,
  cwd: string,
): Promise<SessionExportBundle> {
  const absolutePath = resolve(cwd, path);

  const cached = cache.get(absolutePath);
  if (cached) return cached;

  let stats;
  try {
    stats = await stat(absolutePath);
  } catch (error) {
    throw new UsageError(describeReadFailure(absolutePath, error));
  }

  if (stats.isDirectory()) {
    throw new UsageError(`${absolutePath} is a directory, not a session export file.`);
  }

  if (stats.size > MAX_BUNDLE_BYTES) {
    throw new UsageError(
      `${absolutePath} is ${stats.size} bytes, larger than the ${MAX_BUNDLE_BYTES}-byte limit for a session export.`,
    );
  }

  let raw: string;
  try {
    raw = await readFile(absolutePath, 'utf8');
  } catch (error) {
    throw new UsageError(describeReadFailure(absolutePath, error));
  }

  let document: unknown;
  try {
    document = JSON.parse(raw);
  } catch (error) {
    throw new UsageError(
      `${absolutePath} is not valid JSON: ${error instanceof Error ? error.message : String(error)}`,
    );
  }

  const parsed = parseSessionExportBundle(document);
  if (parsed.isErr()) {
    throw new UsageError(`${absolutePath}: ${parsed.error.message}`);
  }

  cache.set(absolutePath, parsed.value);
  return parsed.value;
}
